import React from 'react';
import * as Yup from 'yup';
import { Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormikWrapper, FormRadioButtons, FormTextField } from '@hvcgroep/design-system';
import JourneyActionButtons from '../../components/JourneyActionButtons';
import { RegistrationDeskStepComponent } from '../../types/multistep-form';
import trackValidationError from '../../../../shared/helpers/trackValidationError';
import { APP_ID } from '../../../../shared/config/constants';

const PaymentDetailsForm: RegistrationDeskStepComponent<'proceed.savePaymentDetails', {}> = (props) => {
  const { t } = useTranslation();
  const {
    onProceed,
    onGoBack,
    context,
  } = props;

  const validationSchema = Yup.object().shape({
    paymentMethod: Yup.string().required(t('registration_desk.screens.payments_details_form.payment_method.error.required')).oneOf(['directDebit', 'ideal']),
    iban: Yup.string().required(t('registration_desk.screens.payments_details_form.iban.error.required')).transform(formatIBAN).test('iban', t('registration_desk.journey.entering_payment_details.invalid_iban'), validateIBAN),
  });

  return (
    <FormikWrapper
      initialValues={{
        paymentMethod: context.paymentMethod ?? '',
        iban: context.iban ? formatIBAN(context.iban) : '',
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values) => {
        onProceed({
          paymentMethod: values.paymentMethod as 'directDebit' | 'ideal',
          iban: formatIBAN(values.iban),
        });
      }}
      onValidationError={(errors) => {
        trackValidationError(String(APP_ID), 'PaymentDetailsForm', errors);
      }}
    >
      {({
        values,
      }) => {
        return (
          <Form noValidate>
            <FormRadioButtons
              name="paymentMethod"
              label={t('registration_desk.screens.payments_details_form.payment_method.label')}
              isLiveValidationDisabled
              options={[
                { value: t('registration_desk.screens.payments_details_form.payment_method.options.direct_debit'), key: 'directDebit' },
                { value: t('registration_desk.screens.payments_details_form.payment_method.options.ideal'), key: 'ideal' },
              ]}
            />
            <FormTextField
              name="iban"
              isLiveValidationDisabled
              description={t('registration_desk.screens.payments_details_form.iban.description')}
              placeholder={t('registration_desk.screens.payments_details_form.iban.placeholder')}
              label={t('registration_desk.screens.payments_details_form.iban.label')}
            />
            <JourneyActionButtons
              onGoBack={() => {
                if (!onGoBack) return;

                onGoBack({
                  paymentMethod: values.paymentMethod as 'directDebit' | 'ideal',
                  iban: formatIBAN(values.iban),
                });
              }}
            />
          </Form>
        );
      }}
    </FormikWrapper>
  );
};

function formatIBAN(iban: string) {
  if (!iban) {
    return '';
  }

  const cleanIban = iban
    .replace(/\s\s+/g, ' ')
    .replace(/[^0-9a-zA-Z]/gi, '')
    .toUpperCase();

  const parts = [];

  if (cleanIban.length > 0) {
    parts.push(cleanIban.substring(0, 4));
  }
  if (cleanIban.length > 4) {
    parts.push(cleanIban.substring(4, 8));
  }
  if (cleanIban.length > 8) {
    parts.push(cleanIban.substring(8, 12));
  }
  if (cleanIban.length > 12) {
    parts.push(cleanIban.substring(12, 16));
  }
  if (cleanIban.length > 16) {
    parts.push(cleanIban.substring(16, 20));
  }
  if (cleanIban.length > 20) {
    parts.push(cleanIban.substring(20, 22));
  }

  return parts.join(' ');
}


function validateIBAN(value: string | undefined) {
  if (!value) {
    return false;
  }

  value = value.replace(/\s/g, '');

  if (!/^NL\d{2}[A-Z]{4}\d{10}$/.test(value)) {
    return false;
  }

  value = value.replace(/^(.{4})(.*)$/, '$2$1');
  value = value.replace(/[A-Z]/g, (e) => (e.charCodeAt(0) - 'A'.charCodeAt(0) + 10).toString());

  let sum = 0;
  let exponent = 1;

  for (let i = value.length - 1; i >= 0; i--) {
    sum += exponent * parseInt(value.charAt(i), 10);
    exponent = (exponent * 10) % 97;
  }

  return sum % 97 === 1;
}

export default PaymentDetailsForm;
